import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  PresentationChartBarIcon,
  UserGroupIcon,
  MailIcon 
} from '@heroicons/react/outline';

// Dashboard stats interface
interface DashboardStats {
  totalUsers: number;
  totalSolutions: number;
  totalContacts: number;
  contactsByStatus: {
    new: number;
    inProgress: number;
    completed: number;
  };
  solutionsByTier: {
    bronze: number;
    silver: number;
    gold: number;
  };
  latestContacts: Array<{
    _id: string;
    name: string;
    email: string;
    status: string;
    createdAt: string;
  }>;
}

interface StatsCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
}

const StatsCard: React.FC<StatsCardProps> = ({ title, value, icon }) => (
  <div className="bg-card dark:bg-dark-card rounded-lg shadow p-6">
    <div className="flex items-center">
      <div className="flex-shrink-0 bg-primary/10 dark:bg-primary-300/20 p-3 rounded-full">
        {React.cloneElement(icon as React.ReactElement, { 
          className: "h-6 w-6 text-primary dark:text-white" 
        })}
      </div>
      <div className="ml-4">
        <h2 className="text-lg font-medium text-muted-foreground dark:text-slate-300">
          {title}
        </h2>
        <p className="text-3xl font-bold text-foreground dark:text-slate-100 mt-1">
          {value}
        </p>
      </div>
    </div>
  </div>
);

const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await axios.get<{success: boolean; data: DashboardStats}>('/api/admin/dashboard');
        
        if (response.data.success) {
          setStats(response.data.data);
        } else {
          setError('Failed to load dashboard data');
        }
      } catch (err: any) {
        setError(
          err.response?.data?.error || 
          'An error occurred while fetching dashboard data'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-foreground dark:text-dark-foreground">
          Loading...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 dark:bg-dark-destructive/10 p-6 rounded-lg">
        <h2 className="text-xl font-bold text-destructive dark:text-dark-destructive mb-2">
          Error
        </h2>
        <p className="text-destructive dark:text-dark-destructive">{error}</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="bg-muted dark:bg-dark-muted p-6 rounded-lg">
        <p className="text-foreground dark:text-dark-foreground">
          No data available
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-foreground dark:text-slate-100">
        Dashboard
      </h1>
      
      {/* Top stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatsCard 
          title="Total Solutions" 
          value={stats.totalSolutions}
          icon={<PresentationChartBarIcon className="h-6 w-6" />}
        />
        
        <StatsCard 
          title="Total Users" 
          value={stats.totalUsers}
          icon={<UserGroupIcon className="h-6 w-6" />}
        />
        
        <StatsCard 
          title="Total Contacts" 
          value={stats.totalContacts}
          icon={<MailIcon className="h-6 w-6" />}
        />
      </div>
      
      {/* Solutions by tier */}
      <div className="bg-card dark:bg-dark-card rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-foreground dark:text-slate-100 mb-4">
          Solutions by Tier
        </h2>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-background dark:bg-dark-background rounded p-4">
            <h3 className="text-sm font-medium text-muted-foreground dark:text-slate-300">
              Bronze
            </h3>
            <p className="text-xl font-semibold text-foreground dark:text-slate-100 mt-1">
              {stats.solutionsByTier.bronze}
            </p>
          </div>
          
          <div className="bg-background dark:bg-dark-background rounded p-4">
            <h3 className="text-sm font-medium text-muted-foreground dark:text-slate-300">
              Silver
            </h3>
            <p className="text-xl font-semibold text-foreground dark:text-slate-100 mt-1">
              {stats.solutionsByTier.silver}
            </p>
          </div>
          
          <div className="bg-background dark:bg-dark-background rounded p-4">
            <h3 className="text-sm font-medium text-muted-foreground dark:text-slate-300">
              Gold
            </h3>
            <p className="text-xl font-semibold text-foreground dark:text-slate-100 mt-1">
              {stats.solutionsByTier.gold}
            </p>
          </div>
        </div>
      </div>
      
      {/* Contacts by status */}
      <div className="bg-card dark:bg-dark-card rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-foreground dark:text-slate-100 mb-4">
          Contacts by Status
        </h2>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-background dark:bg-dark-background rounded p-4">
            <h3 className="text-sm font-medium text-muted-foreground dark:text-slate-300">
              New
            </h3>
            <p className="text-xl font-semibold text-foreground dark:text-slate-100 mt-1">
              {stats.contactsByStatus.new}
            </p>
          </div>
          
          <div className="bg-background dark:bg-dark-background rounded p-4">
            <h3 className="text-sm font-medium text-muted-foreground dark:text-slate-300">
              In Progress
            </h3>
            <p className="text-xl font-semibold text-foreground dark:text-slate-100 mt-1">
              {stats.contactsByStatus.inProgress}
            </p>
          </div>
          
          <div className="bg-background dark:bg-dark-background rounded p-4">
            <h3 className="text-sm font-medium text-muted-foreground dark:text-slate-300">
              Completed
            </h3>
            <p className="text-xl font-semibold text-foreground dark:text-slate-100 mt-1">
              {stats.contactsByStatus.completed}
            </p>
          </div>
        </div>
      </div>
      
      {/* Latest contacts */}
      <div className="bg-card dark:bg-dark-card rounded-lg shadow overflow-hidden">
        <div className="p-6">
          <h2 className="text-lg font-medium text-foreground dark:text-slate-100">
            Latest Contacts
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border dark:divide-dark-border">
            <thead className="bg-muted dark:bg-dark-muted">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground dark:text-dark-muted-foreground uppercase tracking-wider">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground dark:text-dark-muted-foreground uppercase tracking-wider">
                  Email
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground dark:text-dark-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground dark:text-dark-muted-foreground uppercase tracking-wider">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-background dark:bg-dark-background divide-y divide-border dark:divide-dark-border">
              {stats.latestContacts.length > 0 ? (
                stats.latestContacts.map((contact) => (
                  <tr key={contact._id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground dark:text-dark-foreground">
                      {contact.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground dark:text-dark-foreground">
                      {contact.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${ 
                        contact.status === 'new' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' : 
                        contact.status === 'inProgress' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' : 
                        'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                      }`}>
                        {contact.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground dark:text-dark-muted-foreground">
                      {new Date(contact.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-center text-sm text-muted-foreground dark:text-dark-muted-foreground">
                    No contacts found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage; 